// ---------------------------------------------------------------------------
// Registro do aparelho: a cada abertura do app (e ao voltar para ele, no
// máximo 1 vez a cada 30 min) marcamos que este aparelho abriu o Se Liga.
// Funciona logado ou não, sem travar a tela e sem mostrar erro nenhum.
// ---------------------------------------------------------------------------

import { supabase } from './supabase';
import { ehAndroid, ehIOSAparelho } from './navegador';
import { emApp } from './app';

const CHAVE_ID = 'seliga_aparelho';
const CHAVE_ULTIMO = 'seliga_aparelho_aberto_em';
const INTERVALO = 30 * 60 * 1000;

// UUID do aparelho guardado no navegador. Se o armazenamento falhar, não
// chamamos o banco (melhor não registrar do que registrar errado).
function idDoAparelho(): string | null {
  try {
    let id = localStorage.getItem(CHAVE_ID);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(CHAVE_ID, id);
    }
    return id;
  } catch {
    return null;
  }
}

type Plataforma = 'android' | 'ios' | 'desktop' | 'outro';

function plataforma(): Plataforma {
  if (ehAndroid) return 'android';
  if (ehIOSAparelho) return 'ios';
  const ua = navigator.userAgent || '';
  if (/Windows|Macintosh|Linux|CrOS/i.test(ua)) return 'desktop';
  return 'outro';
}

type Navegador =
  | 'safari'
  | 'chrome'
  | 'samsung'
  | 'firefox'
  | 'instagram'
  | 'facebook'
  | 'whatsapp'
  | 'outro';

function navegador(): Navegador {
  const ua = navigator.userAgent || '';
  if (/Instagram/i.test(ua)) return 'instagram';
  if (/FBAN|FBAV|FB_IAB|FBIOS/i.test(ua)) return 'facebook';
  if (/WhatsApp/i.test(ua)) return 'whatsapp';
  if (/SamsungBrowser/i.test(ua)) return 'samsung';
  if (/Firefox|FxiOS/i.test(ua)) return 'firefox';
  if (/EdgA|EdgiOS|OPiOS|OPR|CriOS|Chrome|Chromium/i.test(ua)) return 'chrome';
  if (/Safari/i.test(ua)) return 'safari';
  return 'outro';
}

function permissaoPush(): string {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

// forcar = true na abertura (sempre registra); false ao voltar para o app
// (respeita o intervalo de 30 min).
export async function registrarAbertura(forcar = false): Promise<void> {
  const aparelho = idDoAparelho();
  if (!aparelho) return;

  try {
    const ultimo = Number(localStorage.getItem(CHAVE_ULTIMO) || '0') || 0;
    if (!forcar && Date.now() - ultimo < INTERVALO) return;
    localStorage.setItem(CHAVE_ULTIMO, String(Date.now()));
  } catch {
    // sem armazenamento: segue e registra assim mesmo
  }

  try {
    await supabase.rpc('app_registrar_abertura', {
      p_aparelho: aparelho,
      p_plataforma: plataforma(),
      p_navegador: navegador(),
      p_instalado: emApp(),
      p_push: permissaoPush(),
    });
  } catch {
    // silencioso: registrar abertura nunca atrapalha a pessoa
  }
}

// Liga o registro: uma vez ao abrir e, ao voltar para o app, no máximo a cada
// 30 minutos. Devolve a função que desliga (para o useEffect).
export function iniciarRegistroAparelho(): () => void {
  void registrarAbertura(true);
  const aoVoltar = () => {
    if (document.visibilityState === 'visible') void registrarAbertura(false);
  };
  document.addEventListener('visibilitychange', aoVoltar);
  return () => document.removeEventListener('visibilitychange', aoVoltar);
}